import React, { useCallback, useEffect } from "react";
import { Tooltip, Box, Grid, IconButton } from "@mzc-pdc/ui";
import { useTranslation } from "react-i18next";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import SelectFolderPath from "./select-folder-popper";
import { IconAdd, ImageFolderSmallOn } from "@mzc-cloudplex/icons";
import Typography from "@components_v2/typography/typography";
import { useDispatch, useSelector } from "react-redux";
import { getDriveData } from "@modules/folderTreeView";
import theme from "@styles/theme";

const FolderInput = ({
    domain,
    label,
    useDefaultFolder,
    projectId,
    disablePortal,
    placement,
    value,
    onSubmit,
    sx,
    ...props
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const stage = useSelector(({ stage }) => stage);
    const [popperOpen, setPopperOpen] = React.useState(false);

    const popperPosRef = React.useRef(null);
    const handleClose = () => {
        setPopperOpen(false);
    };
    const onClickSelectFolderPopperOpen = (e) => {
        setPopperOpen(true);
    };
    const onChangeFolderPath = (path) => {
        setPopperOpen(false);
        onSubmit(path);
    };

    const getRootFolderId = useCallback(async () => {
        const driveList = await dispatch(getDriveData({ headerParams: { projectId }, domain: "ASSET" }));
        return { defaultFolder: driveList[0] };
    }, [stage, projectId]);

    useEffect(async () => {
        // drive folder를 default로 설정
        if (!!value || !useDefaultFolder) return;
        const rootFolder = await getRootFolderId();
        onSubmit(rootFolder);
    }, []);

    useEffect(async () => {
        if (useDefaultFolder) {
            const rootFolder = await getRootFolderId();
            onSubmit(rootFolder);
        }
    }, [projectId]);

    return (
        <ClickAwayListener mouseEvent={popperOpen ? "onClick" : false} onClickAway={handleClose}>
            <Box>
                <Grid
                    component={"fieldset"}
                    container
                    border={1}
                    borderColor={"greySecondary.light"}
                    borderRadius={1}
                    py={1}
                    pl={2}
                    pr={1.5}
                    gap={2.5}
                    flexWrap={"nowrap"}
                    alignItems={"center"}
                    ref={popperPosRef}
                    sx={{ height: sx?.height ?? "100%" }}
                >
                    {label && (
                        <legend>
                            <Typography variant="body4_400" px={0.5} color={theme.palette.greySecondary[600]}>
                                {label}
                                <span
                                    className={"MuiFormLabel-asterisk MuiInputLabel-asterisk"}
                                    style={{ paddingLeft: "2px", color: theme.palette.error[700] }}
                                >
                                    *
                                </span>
                            </Typography>
                        </legend>
                    )}
                    <Grid item xs mt={label ? -1 : 0}>
                        {!value?.folderName ? (
                            <Typography variant={"body1"} color={"greySecondary.400"}>
                                {t("common::label::Save to top path")}
                            </Typography>
                        ) : (
                            <Grid container flexDirection={"row"} gap={0.5} alignItems={"center"}>
                                <Grid item height={16}>
                                    <ImageFolderSmallOn size={16} />
                                </Grid>
                                <Typography>{value?.folderName}</Typography>
                            </Grid>
                        )}
                    </Grid>
                    <Grid item zeroMinWidth mt={label ? -1 : 0}>
                        <Grid container gap={0.75} flexWrap={"nowrap"}>
                            <Tooltip title={t(`common::label::Add`, `Add`)}>
                                <IconButton
                                    circled
                                    size={"small"}
                                    onClick={(e) => {
                                        e.preventDefault();

                                        onClickSelectFolderPopperOpen();
                                    }}
                                >
                                    <IconAdd size={16} />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Grid>

                {popperOpen && (
                    <SelectFolderPath
                        domain={domain}
                        projectId={projectId}
                        open={popperOpen}
                        submitText={`Select`}
                        title={`Select Folder Path`}
                        onSubmit={onChangeFolderPath}
                        onClose={() => setPopperOpen(false)}
                        subFoldersOption={false}
                        disablePortal={disablePortal}
                        anchorEl={popperPosRef.current}
                        wrapperSx={{
                            width: "688px",
                        }}
                    />
                )}
            </Box>
        </ClickAwayListener>
    );
};

export default FolderInput;
